import React, { useEffect } from "react";
import BaseModal from "@/components/reusables/Modals/BaseModal";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import BaseInput from "@reusables/BaseInput";
import SettingsSection
    from "@components/Dashboard/pages/SettingsPage/Integrations/ThirdPartyIntegrations/components/SettingsSection";
import { useGetCategoriesQuery } from "@redux/features/categories/categoriesApi";
import BaseDropdown, { adaptInfiniteLoader } from "@reusables/dropdowns/BaseDropdown";
import { productsApi } from "@redux/features/products/productsApi";
import { useAppDispatch } from "@redux/hooks";
import { BadgeStatus, CustomerGroups, Product, WooCommerce } from "@/types/general";
import ImmutableValue from "./ImmutableValue";
import BaseOrderStatusBadge from "@reusables/BaseOrderStatusBadge";
import BaseMaterialCheckbox from "@reusables/BaseMaterialCheckbox";
import { useGetPaymentTermsQuery } from "@redux/features/paymentTerms/paymentTermsApi";
import { customerGroupsApi } from "@redux/features/customerGroups/customerGroupsApi";
import { toastError } from "@helpers/utils";
import { useGetTaxesQuery } from "@redux/features/taxes/taxesApi";
import BaseButton from "@reusables/BaseButton";
import {
    useConnectWooCommerceMutation,
    useGetWooCommerceConfigurationQuery,
    useUpdateWooCommerceConfigurationMutation
} from "@redux/features/integrations/integrationsApi";
import BaseInfiniteLoaderHandler from "@reusables/dropdowns/BaseInfiniteLoader/BaseInfiniteLoaderHandler";

const formScheme = z.object({
    consumer_key: z.string(),
    consumer_secret: z.string(),
    shop_url: z.string().url(),
    configuration: z.object({
        product_categories: z.array(z.number()),
        product_for_fees: z.number().nullish(),
        product_for_shipping: z.number().nullish(),
        marked_completed_orders: z.boolean(),
        standard_payment_term: z.number().nullish(),
        standard_customer_group: z.number().nullish(),
        standard_tax: z.number().nullish()
    })
});

type FormTyping = z.infer<typeof formScheme>;

type WooCommerceIntegrationModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

export default function WooCommerceIntegrationModal({ isOpen, onClose }: WooCommerceIntegrationModalProps) {
    const dispatch = useAppDispatch();

    const { t } = useTranslation("", { keyPrefix: "settings.integrations.thirdParty.integrations.woocommerce.integrationModal" });

    const form = useForm<FormTyping>({
        resolver: zodResolver(formScheme),
        defaultValues: {
            configuration: {
                marked_completed_orders: true
            }
        }
    });

    const { data: categories, isLoading: isCategoriesLoading } = useGetCategoriesQuery();

    const { data: paymentTerms, isLoading: isPaymentTermsLoading } = useGetPaymentTermsQuery();

    const { data: taxesOptions, isLoading: isTaxesLoading } = useGetTaxesQuery();

    const [connectIntegration, { isLoading: isConnectionLoading }] = useConnectWooCommerceMutation();
    const [updateIntegration, { isLoading: isUpdateLoading }] = useUpdateWooCommerceConfigurationMutation();

    const {
        data: currentConfiguration,
        isLoading: isCurrentConfigurationLoading
    } = useGetWooCommerceConfigurationQuery();

    useEffect(() => {
        if (isOpen && currentConfiguration) {
            const { setValue } = form;
            setValue("consumer_key", currentConfiguration?.consumer_key ?? "");
            setValue("consumer_secret", currentConfiguration?.consumer_secret ?? "");
            setValue("shop_url", currentConfiguration?.shop_url ?? "");

            const productCategories = currentConfiguration?.configuration?.product_categories ?? [];
            setValue("configuration.product_categories", productCategories);

            const productForFees = currentConfiguration?.configuration?.product_for_fees ?? undefined;
            setValue("configuration.product_for_fees", productForFees);

            const productForShipping = currentConfiguration?.configuration?.product_for_shipping ?? undefined;
            setValue("configuration.product_for_shipping", productForShipping);

            const markedCompletedOrders = currentConfiguration?.configuration?.marked_completed_orders ?? false;
            setValue("configuration.marked_completed_orders", markedCompletedOrders);

            const standardPaymentTerm = currentConfiguration?.configuration?.standard_payment_term ?? undefined;
            setValue("configuration.standard_payment_term", standardPaymentTerm);

            const standardCustomerGroup = currentConfiguration?.configuration?.standard_customer_group ?? undefined;
            setValue("configuration.standard_customer_group", standardCustomerGroup);

            const standardTax = currentConfiguration?.configuration?.standard_tax ?? undefined;
            setValue("configuration.standard_tax", standardTax);
        } else {
            form.reset();
        }
    }, [isOpen]);

    const onSubmit = form.handleSubmit((data) => {
        const integrationConfigObject: WooCommerce.DTO.Connect & WooCommerce.DTO.UpdateConfiguration = {
            consumer_key: data.consumer_key,
            consumer_secret: data.consumer_secret,
            shop_url: data.shop_url,
            configuration: {
                product_categories: data.configuration.product_categories,
                product_for_fees: data.configuration.product_for_fees,
                product_for_shipping: data.configuration.product_for_shipping,
                marked_completed_orders: data.configuration.marked_completed_orders,
                standard_payment_term: data.configuration.standard_payment_term,
                standard_customer_group: data.configuration.standard_customer_group,
                standard_tax: data.configuration.standard_tax
            }
        };

        if (currentConfiguration) {
            updateIntegration(integrationConfigObject)
                .unwrap()
                .then(() => {
                    onClose();
                })
                .catch((e) => {
                    toastError(e);
                });
        } else {
            connectIntegration(integrationConfigObject)
                .unwrap()
                .then(() => {
                    onClose();
                })
                .catch((e) => {
                    toastError(e);
                });
        }
    }, console.error);

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            width={800}
            padding="45px"
            useCloseIcon
            isLoading={isConnectionLoading || isUpdateLoading || isCurrentConfigurationLoading}
        >
            <div
                className="text-xl font-semibold text-center text-accent mb-4">
                {t("heading")}
            </div>
            <FormProvider {...form}>
                <form onSubmit={onSubmit} className="space-y-4">
                    {/* # Consumer Key # */}
                    <Controller
                        control={form.control}
                        name={"consumer_key"}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("connection.consumer_key.label")}
                                placeholder={t("connection.consumer_key.placeholder")}
                                required
                            />
                        )}
                    />

                    {/* # Consumer Secret # */}
                    <Controller
                        control={form.control}
                        name={"consumer_secret"}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("connection.consumer_secret.label")}
                                placeholder={t("connection.consumer_secret.placeholder")}
                                required
                            />
                        )}
                    />

                    {/* # Shop URL # */}
                    <Controller
                        control={form.control}
                        name={"shop_url"}
                        render={({ field, fieldState }) =>
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("connection.shop_url.label")}
                                placeholder={t("connection.shop_url.placeholder")}
                                required
                            />
                        }
                    />

                    <SettingsSection collapsible label={t("product_settings.heading")}>
                        {/* # Product Categories # */}
                        <Controller
                            control={form.control}
                            name={"configuration.product_categories"}
                            render={({ field, fieldState }) => (
                                <BaseDropdown
                                    {...field}
                                    {...fieldState}
                                    onChange={(_, newState) => field.onChange(newState.map((opt) => opt.id))}
                                    options={categories}
                                    getter={{
                                        key: (opt) => opt.id,
                                        label: (opt) => opt.name
                                    }}
                                    label={t("product_settings.product_categories.label")}
                                    placeholder={t("product_settings.product_categories.placeholder")}
                                    multiple
                                    autocomplete
                                    isLoading={isCategoriesLoading}
                                    required
                                />
                            )}
                        />

                        {/* # Product for Fees # */}
                        <BaseInfiniteLoaderHandler<Product.Slim>
                            fetcher={(search, page, limit) =>
                                dispatch(
                                    productsApi.endpoints.getModuleProductsFull.initiate(
                                        {
                                            filters: {
                                                search,
                                                type: 1
                                            },
                                            pagination: {
                                                page,
                                                limit
                                            },
                                            module: "purchase-order"
                                        },
                                        {
                                            subscribe: false
                                        }
                                    )
                                )
                                    .unwrap()
                                    .then((res) => res?.payload)
                            }
                        >
                            {(infinity) => (
                                <Controller
                                    control={form.control}
                                    name={"configuration.product_for_fees"}
                                    render={({ field, fieldState }) => (
                                        <BaseDropdown
                                            {...field}
                                            {...fieldState}
                                            {...adaptInfiniteLoader(infinity)}
                                            onChange={(opt) => field.onChange(opt?.id)}
                                            getter={{
                                                key: (opt) => opt.id,
                                                label: (opt) => opt.name,
                                                caption: (opt) => opt.code
                                            }}
                                            label={t("product_settings.product_for_fees.label")}
                                            placeholder={t("product_settings.product_for_fees.placeholder")}
                                            autocomplete
                                            virtualize
                                        />
                                    )}
                                />
                            )}
                        </BaseInfiniteLoaderHandler>

                        {/* # Product for Shipping # */}
                        <BaseInfiniteLoaderHandler<Product.Slim>
                            fetcher={(search, page, limit) =>
                                dispatch(
                                    productsApi.endpoints.getModuleProductsFull.initiate(
                                        {
                                            filters: {
                                                search,
                                                type: 1
                                            },
                                            pagination: {
                                                page,
                                                limit
                                            },
                                            module: "purchase-order"
                                        },
                                        {
                                            subscribe: false
                                        }
                                    )
                                )
                                    .unwrap()
                                    .then((res) => res?.payload)
                            }
                        >
                            {(infinity) => (
                                <Controller
                                    control={form.control}
                                    name={"configuration.product_for_shipping"}
                                    render={({ field, fieldState }) => (
                                        <BaseDropdown
                                            {...field}
                                            {...fieldState}
                                            {...adaptInfiniteLoader(infinity)}
                                            onChange={(opt) => field.onChange(opt?.id)}
                                            getter={{
                                                key: (opt) => opt.id,
                                                label: (opt) => opt.name,
                                                caption: (opt) => opt.code
                                            }}
                                            label={t("product_settings.product_for_shipping.label")}
                                            placeholder={t("product_settings.product_for_shipping.placeholder")}
                                            autocomplete
                                            virtualize
                                        />
                                    )}
                                />
                            )}
                        </BaseInfiniteLoaderHandler>

                        {/* # Stock Status for Synchronized Inventories # */}
                        <ImmutableValue
                            label={t("product_settings.stock_status.label")}
                            value={t("product_settings.stock_status.value")}
                            type={"dropdown"}
                        />

                        {/* # Only Sync Inventory from Specific Location # */}
                        <ImmutableValue
                            label={t("product_settings.sync_location.label")}
                            value={t("product_settings.sync_location.value")}
                            type={"dropdown"}
                        />

                        {/* # Get Product Sales Price From # */}
                        <ImmutableValue
                            label={t("product_settings.sales_price_from.label")}
                            value={t("product_settings.sales_price_from.value")}
                            type={"dropdown"}
                        />

                        {/* # Get Product Sales Price From (Duplicate) # */}
                        <ImmutableValue
                            label={t("product_settings.sales_price_from.label")}
                            value={t("product_settings.sales_price_from.value")}
                            type={"dropdown"}
                        />
                    </SettingsSection>

                    <SettingsSection collapsible label={t("customers.heading")}>
                        {/* # Standard Payment Term # */}
                        <Controller
                            control={form.control}
                            name={"configuration.standard_payment_term"}
                            render={({ field, fieldState }) => (
                                <BaseDropdown
                                    label={t("customers.standard_payment_term.label")}
                                    placeholder={t("customers.standard_payment_term.placeholder")}
                                    {...field}
                                    {...fieldState}
                                    onChange={(opt) => field.onChange(opt?.id)}
                                    options={paymentTerms}
                                    getter={{
                                        key: (opt) => opt.id,
                                        label: (opt) => opt.name,
                                        caption: (opt) => opt.code
                                    }}
                                    isLoading={isPaymentTermsLoading}
                                    autocomplete
                                />
                            )}
                        />

                        {/* # Standard Customer Group # */}
                        <BaseInfiniteLoaderHandler<CustomerGroups.Root>
                            fetcher={(search, page, limit) =>
                                dispatch(
                                    customerGroupsApi.endpoints.getCustomerGroups.initiate(
                                        {
                                            filters: {
                                                search
                                            },
                                            pagination: {
                                                page,
                                                limit
                                            }
                                        },
                                        {
                                            subscribe: false
                                        }
                                    )
                                )
                                    .unwrap()
                                    .then((res) => res?.payload)
                            }
                        >
                            {(infinity) => (
                                <Controller
                                    control={form.control}
                                    name={"configuration.standard_customer_group"}
                                    render={({ field, fieldState }) => (
                                        <BaseDropdown
                                            {...field}
                                            {...fieldState}
                                            {...adaptInfiniteLoader(infinity)}
                                            onChange={(opt) => field.onChange(opt?.id)}
                                            getter={{
                                                key: (opt) => opt.id,
                                                label: (opt) => opt.name,
                                                caption: (opt) => opt.code
                                            }}
                                            label={t("customers.standard_customer_group.label")}
                                            placeholder={t("customers.standard_customer_group.placeholder")}
                                            autocomplete
                                            virtualize
                                        />
                                    )}
                                />
                            )}
                        </BaseInfiniteLoaderHandler>

                        {/* # Standard Tax # */}
                        <Controller
                            control={form.control}
                            name={"configuration.standard_tax"}
                            render={({ field, fieldState }) => (
                                <BaseDropdown
                                    {...field}
                                    {...fieldState}
                                    onChange={(opt) => field.onChange(opt?.id)}
                                    getter={{
                                        key: (opt) => opt.id,
                                        label: (opt) => `${opt.name} (${opt.rate}%)`,
                                        caption: (opt) => opt.code
                                    }}
                                    options={taxesOptions}
                                    label={t("customers.standard_tax.label")}
                                    placeholder={t("customers.standard_tax.placeholder")}
                                    isLoading={isTaxesLoading}
                                    autocomplete
                                />
                            )}
                        />
                    </SettingsSection>

                    <SettingsSection collapsible label={t("orders.heading")}>
                        {/* # Status of New Orders Created in Suppli # */}
                        <ImmutableValue
                            label={t("orders.status_new_orders.label")}
                            value={
                                <div>
                                    <BaseOrderStatusBadge status={BadgeStatus.Base.new} />
                                </div>
                            }
                            type={"custom"}
                        />

                        {/* # Status of WooCommerce Orders to Synchronize # */}
                        <ImmutableValue
                            label={t("orders.status_woocommerce_sync.label")}
                            value={t("orders.status_woocommerce_sync.value")}
                            type={"dropdown"}
                        />

                        {/* # Mark Order as Completed in WooCommerce # */}
                        <Controller
                            control={form.control}
                            name={"configuration.marked_completed_orders"}
                            render={({ field, fieldState }) => (
                                <div
                                    className={"flex items-center space-x-2 text-accent cursor-pointer user-select-none"}
                                    onClick={() => field.onChange(!field.value)}
                                >
                                    <BaseMaterialCheckbox checked={field.value} />
                                    <div>{t("orders.mark_completed_orders.label")}</div>
                                </div>
                            )}
                        />
                    </SettingsSection>

                    <div className={"mt-8 text-center"}>
                        <BaseButton
                            buttonWidth="100%"
                            text={t("buttons.activate")}
                            size="md"
                            type="submit"
                        />
                    </div>
                </form>
            </FormProvider>
        </BaseModal>
    );
}
